import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Share2, Instagram, Twitter, Facebook, Youtube, Music2,
  Plus, Calendar, Send, Edit2, Trash2, Clock, CheckCircle,
  Loader2, Image, Video, X, Search
} from 'lucide-react';
import './StaffSocialHub.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: '#1DA1F2' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000' },
  { id: 'tiktok', name: 'TikTok', icon: Music2, color: '#000000' }
];

const POST_TYPES = [
  { id: 'image', name: 'Image Post', icon: Image },
  { id: 'video', name: 'Video', icon: Video },
  { id: 'story', name: 'Story', icon: Clock },
  { id: 'reel', name: 'Reel', icon: Video }
];

const StaffSocialHub = () => {
  const [artists, setArtists] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [filter, setFilter] = useState('all'); // all, draft, scheduled, posted
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    artist_account_id: '',
    artist_name: '',
    platform: 'instagram',
    post_type: 'image',
    caption: '',
    hashtags: '',
    scheduled_at: ''
  });

  const getToken = () => localStorage.getItem('adminToken');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${getToken()}` };
      
      const [artistsRes, postsRes] = await Promise.all([
        fetch(`${API_URL}/api/staff/artists`, { headers }),
        fetch(`${API_URL}/api/staff/social-hub/posts`, { headers })
      ]);

      if (artistsRes.ok) {
        const artistsData = await artistsRes.json();
        setArtists(artistsData);
      }

      if (postsRes.ok) {
        const postsData = await postsRes.json();
        setPosts(postsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.artist_name) {
      toast.error('Please select an artist');
      return;
    }

    if (!formData.caption.trim()) {
      toast.error('Please enter a caption');
      return;
    }

    try {
      const url = editingPost 
        ? `${API_URL}/api/staff/social-hub/posts/${editingPost.id}`
        : `${API_URL}/api/staff/social-hub/posts`;

      const res = await fetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          hashtags: formData.hashtags.split(',').map(h => h.trim()).filter(Boolean)
        })
      });

      if (res.ok) {
        toast.success(editingPost ? 'Post updated!' : 'Post created!');
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save post');
      }
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const res = await fetch(`${API_URL}/api/staff/social-hub/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (res.ok) {
        toast.success('Post deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleMarkPosted = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/social-hub/posts/${postId}/mark-posted`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (res.ok) {
        toast.success('Marked as posted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const resetForm = () => {
    setFormData({
      artist_account_id: '',
      artist_name: '',
      platform: 'instagram',
      post_type: 'image',
      caption: '',
      hashtags: '',
      scheduled_at: ''
    });
    setEditingPost(null);
  };

  const openEditModal = (post) => {
    setEditingPost(post);
    setFormData({
      artist_account_id: post.artist_account_id || '',
      artist_name: post.artist_name,
      platform: post.platform,
      post_type: post.post_type,
      caption: post.caption,
      hashtags: post.hashtags?.join(', ') || '',
      scheduled_at: post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : ''
    });
    setShowModal(true);
  };

  const handleArtistSelect = (artistId) => {
    const artist = artists.find(a => a.id === artistId);
    if (artist) {
      setFormData({
        ...formData,
        artist_account_id: artist.id,
        artist_name: artist.artist_name
      });
    }
  };

  const filteredPosts = posts.filter(post => {
    if (filter !== 'all' && post.status !== filter) return false;
    if (searchQuery && !post.artist_name.toLowerCase().includes(searchQuery.toLowerCase()) 
        && !post.caption.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const getPlatformIcon = (platformId) => {
    const platform = PLATFORMS.find(p => p.id === platformId);
    return platform ? platform.icon : Share2;
  };

  const getPlatformColor = (platformId) => {
    const platform = PLATFORMS.find(p => p.id === platformId);
    return platform ? platform.color : '#00ffff';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="social-hub-loading">
        <Loader2 size={40} className="spinner" />
        <p>Loading Social Hub...</p>
      </div>
    );
  }

  return (
    <div className="staff-social-hub" data-testid="staff-social-hub">
      <div className="hub-header">
        <div className="hub-title">
          <Share2 size={28} />
          <h1>Social Hub</h1>
        </div>
        <p>Create and manage social media posts for artists</p>
      </div>

      {/* Stats Bar */}
      <div className="hub-stats">
        <div className="stat-card">
          <span className="stat-value">{posts.filter(p => p.status === 'draft').length}</span>
          <span className="stat-label">Drafts</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{posts.filter(p => p.status === 'scheduled').length}</span>
          <span className="stat-label">Scheduled</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{posts.filter(p => p.status === 'posted').length}</span>
          <span className="stat-label">Posted</span>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="hub-actions">
        <button 
          className="btn btn-primary create-btn"
          onClick={() => { resetForm(); setShowModal(true); }}
          data-testid="create-post-btn"
        >
          <Plus size={18} />
          Create Post
        </button>

        <div className="hub-filters">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Posts</option>
            <option value="draft">Drafts</option>
            <option value="scheduled">Scheduled</option>
            <option value="posted">Posted</option>
          </select>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="posts-grid">
        {filteredPosts.length === 0 ? (
          <div className="no-posts">
            <Share2 size={48} />
            <p>No posts found</p>
            <button className="btn btn-secondary" onClick={() => { resetForm(); setShowModal(true); }}>
              Create your first post
            </button>
          </div>
        ) : (
          filteredPosts.map(post => {
            const PlatformIcon = getPlatformIcon(post.platform);
            return (
              <div key={post.id} className={`post-card status-${post.status}`} data-testid="post-card">
                <div className="post-header">
                  <div className="post-platform" style={{ backgroundColor: getPlatformColor(post.platform) }}>
                    <PlatformIcon size={16} />
                  </div>
                  <div className="post-artist">
                    <span className="artist-name">{post.artist_name}</span>
                    <span className="post-type">{post.post_type}</span>
                  </div>
                  <div className={`post-status ${post.status}`}>
                    {post.status === 'posted' && <CheckCircle size={14} />}
                    {post.status === 'scheduled' && <Clock size={14} />}
                    {post.status}
                  </div>
                </div>

                <div className="post-content">
                  <p>{post.caption}</p>
                  {post.hashtags?.length > 0 && (
                    <div className="post-hashtags">
                      {post.hashtags.map((tag, i) => (
                        <span key={i} className="hashtag">#{tag}</span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="post-meta">
                  {post.scheduled_at && (
                    <span className="scheduled-time">
                      <Calendar size={14} />
                      {formatDate(post.scheduled_at)}
                    </span>
                  )}
                  <span className="created-by">by {post.created_by}</span>
                </div>

                <div className="post-actions">
                  {post.status !== 'posted' && (
                    <>
                      <button 
                        className="action-btn edit"
                        onClick={() => openEditModal(post)}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="action-btn post"
                        onClick={() => handleMarkPosted(post.id)}
                        title="Mark as Posted"
                      >
                        <Send size={16} />
                      </button>
                    </>
                  )}
                  <button 
                    className="action-btn delete"
                    onClick={() => handleDelete(post.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPost ? 'Edit Post' : 'Create New Post'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="post-form">
              <div className="form-group">
                <label>Artist *</label>
                <select
                  value={formData.artist_account_id}
                  onChange={(e) => handleArtistSelect(e.target.value)}
                  required
                  data-testid="select-artist"
                >
                  <option value="">Select Artist</option>
                  {artists.filter(a => a.status === 'approved').map(artist => (
                    <option key={artist.id} value={artist.id}>{artist.artist_name}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Platform *</label>
                  <div className="platform-selector">
                    {PLATFORMS.map(platform => {
                      const Icon = platform.icon;
                      return (
                        <button
                          key={platform.id}
                          type="button"
                          className={`platform-btn ${formData.platform === platform.id ? 'active' : ''}`}
                          onClick={() => setFormData({...formData, platform: platform.id})}
                          style={{ '--platform-color': platform.color }}
                        >
                          <Icon size={20} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="form-group">
                  <label>Post Type *</label>
                  <select
                    value={formData.post_type}
                    onChange={(e) => setFormData({...formData, post_type: e.target.value})}
                  >
                    {POST_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Caption *</label>
                <textarea
                  value={formData.caption}
                  onChange={(e) => setFormData({...formData, caption: e.target.value})}
                  placeholder="Write your post caption..."
                  rows={5}
                  required
                  data-testid="post-caption"
                />
                <span className="char-count">{formData.caption.length} characters</span>
              </div>

              <div className="form-group">
                <label>Hashtags (comma-separated)</label>
                <input
                  type="text"
                  value={formData.hashtags}
                  onChange={(e) => setFormData({...formData, hashtags: e.target.value})}
                  placeholder="melodictechno, newrelease, amr"
                />
              </div>

              <div className="form-group">
                <label>Schedule (optional)</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({...formData, scheduled_at: e.target.value})}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="submit-post">
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffSocialHub;
